import { useState, useMemo, useRef, FC } from 'react';
import {
  Select,
  SelectItem,
  Card,
  CardBody,
  CardHeader,
  Tabs,
  Tab,
  Divider,
} from '@nextui-org/react';
import { Settlement } from 'common/src/utils/types';
import { groupSettlementsByWeek } from 'common/src/utils/functions';
import ComparativeBarChart from './ComparativeBarChart';
import useDataStore from '../../../../../common/src/stores/useDataStore';
import { useTranslation } from '../../../../../common/src/hooks/useTranslation';
import ChartDownloadButton from '../../../../../common/src/components/charts/ChartDownloadButton';

// --- Types ---
type GroupByKey =
  | 'week'
  | 'variety'
  | 'format'
  | 'receiver'
  | 'caliber'
  | 'color'
  | 'market'
  | 'label';
type ChartMetric = 'sales' | 'volume' | 'price';

// --- Constants ---
const TABS_ORDER: GroupByKey[] = [
  'week',
  'variety',
  'format',
  'receiver',
  'caliber',
  'color',
  'market',
  'label',
];

// --- Main Component ---
interface ComparativeAnalysisSectionProps {
  settlementsA: Settlement[];
  settlementsB: Settlement[];
  seasonA: string;
  seasonB: string;
  priceView?: string;
  dateType?: 'eta' | 'etd' | 'sale_date';
}
const ComparativeAnalysisSection: FC<ComparativeAnalysisSectionProps> = ({
  settlementsA,
  settlementsB,
  seasonA,
  seasonB,
  priceView = 'kg',
  dateType = 'eta',
}) => {
  const { t } = useTranslation('settlementsComparative');
  const [groupBy, setGroupBy] = useState<GroupByKey>('week');
  const salesChartRef = useRef<HTMLDivElement>(null);
  const volumeChartRef = useRef<HTMLDivElement>(null);
  const priceChartRef = useRef<HTMLDivElement>(null);
  const [salesViewMode, setSalesViewMode] = useState<'absolute' | 'percentage'>(
    'absolute'
  );
  const [volumeViewMode, setVolumeViewMode] = useState<
    'absolute' | 'percentage'
  >('absolute');
  const { selectedSpecies } = useDataStore();

  const GROUP_BY_OPTIONS: Record<GroupByKey, string> = {
    week: t('Por Semana'),
    variety: t('Por Variedad'),
    format: t('Por Formato'),
    receiver: t('Por Cliente'),
    caliber: t('Por Calibre'),
    color: t('Por Color'),
    market: t('Por Mercado'),
    label: t('Por Etiqueta'),
  };

  const { salesChartData, volumeChartData, priceChartData } = useMemo(() => {
    const allSettlements = [...settlementsA, ...settlementsB];

    const getDate = (settlement: Settlement): string => {
      if (dateType === 'eta') {
        return settlement.ship_trip?.eta || settlement.date_sold;
      } else if (dateType === 'etd') {
        return settlement.ship_trip?.etd || settlement.date_sold;
      } else {
        return settlement.date_sold;
      }
    };

    const groupedByWeek = groupSettlementsByWeek(allSettlements, getDate);

    const settlementIdToWeekMap = new Map<string, string>();
    for (const week in groupedByWeek) {
      for (const settlement of groupedByWeek[week]) {
        settlementIdToWeekMap.set(String(settlement.id), week);
      }
    }

    const getGenericWeek = (
      fullWeek: string | undefined
    ): string | undefined => {
      if (!fullWeek) return undefined;
      const parts = fullWeek.split('-');
      return `W${parts[parts.length - 1].replace('W', '')}`;
    };

    const sortWeeksCustom = (a: string, b: string): number => {
      const numA = parseInt(a.slice(1), 10);
      const numB = parseInt(b.slice(1), 10);

      const groupA = numA >= 24 ? 1 : 2;
      const groupB = numB >= 24 ? 1 : 2;

      if (groupA !== groupB) {
        return groupA - groupB;
      }

      return numA - numB;
    };

    const sortCalibersCustom = (a: string, b: string): number => {
      // Si es CHERRY, usar orden específico de calibres de cereza
      if (selectedSpecies?.excel_name === 'CHERRY') {
        const cherryOrder = ['L', 'XL', 'J', '2J', '3J', '4J', '5J'];
        const indexA = cherryOrder.indexOf(a);
        const indexB = cherryOrder.indexOf(b);

        // Si ambos están en la lista de cherry, usar el orden definido
        if (indexA !== -1 && indexB !== -1) {
          return indexA - indexB;
        }

        // Si solo uno está en la lista, el que está en la lista va primero
        if (indexA !== -1) return -1;
        if (indexB !== -1) return 1;
      }

      // Si son números, ordenar de menor a mayor
      const numA = parseInt(a);
      const numB = parseInt(b);
      if (!isNaN(numA) && !isNaN(numB)) {
        return numA - numB;
      }

      // Para cualquier otro caso, orden alfabético por defecto
      return a.localeCompare(b);
    };

    const groupAndCalculateMetric = (
      settlements: Settlement[],
      currentGroupBy: GroupByKey,
      metric: ChartMetric
    ): Record<string, number> => {
      const result: Record<
        string,
        { totalSales: number; totalVolume: number }
      > = {};

      settlements.forEach((s) => {
        s.settlement_details.forEach((d) => {
          let key: string | undefined;
          switch (currentGroupBy) {
            case 'variety':
              key = d.variety;
              break;
            case 'caliber':
              key = d.caliber;
              break;
            case 'color':
              key = d.color;
              break;
            case 'format':
              key = `${d.format}-${d.unit_weight}KG`;
              break;
            case 'label':
              key = d.label;
              break;
            case 'market':
              key = s.market?.name || 'N/A';
              break;
            case 'receiver':
              key = s.receiver;
              break;
            case 'week':
              key = getGenericWeek(settlementIdToWeekMap.get(String(s.id)));
              break;
          }
          if (!key) return;
          if (!result[key]) {
            result[key] = { totalSales: 0, totalVolume: 0 };
          }
          result[key].totalSales += d.usd_total_fob || 0;
          result[key].totalVolume += d.kilos || 0;
        });
      });

      const finalResult: Record<string, number> = {};
      for (const key in result) {
        const group = result[key];
        switch (metric) {
          case 'sales':
            finalResult[key] = group.totalSales;
            break;
          case 'volume':
            finalResult[key] = group.totalVolume;
            break;
          case 'price':
            let price = group.totalVolume > 0 ? group.totalSales / group.totalVolume : 0;
            // Si estamos viendo por caja equivalente, multiplicar por el tamaño de la caja
            if (priceView.startsWith('box-')) {
              const boxSize = parseInt(priceView.split('-')[1]);
              price *= boxSize;
            }
            finalResult[key] = price;
            break;
        }
      }
      return finalResult;
    };

    const salesDataA = groupAndCalculateMetric(settlementsA, groupBy, 'sales');
    const salesDataB = groupAndCalculateMetric(settlementsB, groupBy, 'sales');
    const volumeDataA = groupAndCalculateMetric(
      settlementsA,
      groupBy,
      'volume'
    );
    const volumeDataB = groupAndCalculateMetric(
      settlementsB,
      groupBy,
      'volume'
    );
    const priceDataA = groupAndCalculateMetric(settlementsA, groupBy, 'price');
    const priceDataB = groupAndCalculateMetric(settlementsB, groupBy, 'price');

    const allKeysUnsorted = [
      ...new Set([
        ...Object.keys(salesDataA),
        ...Object.keys(salesDataB),
        ...Object.keys(volumeDataA),
        ...Object.keys(volumeDataB),
        ...Object.keys(priceDataA),
        ...Object.keys(priceDataB),
      ]),
    ];
    const allKeys =
      groupBy === 'week'
        ? allKeysUnsorted.sort(sortWeeksCustom)
        : groupBy === 'caliber'
          ? allKeysUnsorted.sort(sortCalibersCustom)
          : allKeysUnsorted.sort();

    const salesChartData = {
      series:
        salesViewMode === 'absolute'
          ? [
            {
              name: seasonA,
              data: allKeys.map((key) => salesDataA[key] || 0),
            },
            {
              name: seasonB,
              data: allKeys.map((key) => salesDataB[key] || 0),
            },
          ]
          : [
            {
              name: seasonA,
              data: allKeys.map((key) => {
                const value = salesDataA[key] || 0;
                const total = Object.values(salesDataA).reduce(
                  (sum, val) => sum + val,
                  0
                );
                return total > 0 ? (value / total) * 100 : 0;
              }),
            },
            {
              name: seasonB,
              data: allKeys.map((key) => {
                const value = salesDataB[key] || 0;
                const total = Object.values(salesDataB).reduce(
                  (sum, val) => sum + val,
                  0
                );
                return total > 0 ? (value / total) * 100 : 0;
              }),
            },
          ],
      categories: allKeys,
    };

    const volumeChartData = {
      series:
        volumeViewMode === 'absolute'
          ? [
            {
              name: seasonA,
              data: allKeys.map((key) => volumeDataA[key] || 0),
            },
            {
              name: seasonB,
              data: allKeys.map((key) => volumeDataB[key] || 0),
            },
          ]
          : [
            {
              name: seasonA,
              data: allKeys.map((key) => {
                const value = volumeDataA[key] || 0;
                const total = Object.values(volumeDataA).reduce(
                  (sum, val) => sum + val,
                  0
                );
                return total > 0 ? (value / total) * 100 : 0;
              }),
            },
            {
              name: seasonB,
              data: allKeys.map((key) => {
                const value = volumeDataB[key] || 0;
                const total = Object.values(volumeDataB).reduce(
                  (sum, val) => sum + val,
                  0
                );
                return total > 0 ? (value / total) * 100 : 0;
              }),
            },
          ],
      categories: allKeys,
    };

    const priceChartData = {
      series: [
        { name: seasonA, data: allKeys.map((key) => priceDataA[key] || 0) },
        { name: seasonB, data: allKeys.map((key) => priceDataB[key] || 0) },
      ],
      categories: allKeys,
    };

    return {
      salesChartData,
      volumeChartData,
      priceChartData,
    };
  }, [
    settlementsA,
    settlementsB,
    groupBy,
    seasonA,
    seasonB,
    salesViewMode,
    volumeViewMode,
    selectedSpecies,
    dateType,
    priceView,
  ]);

  return (
    <Card>
      <CardHeader className='flex-col items-start px-6 pt-6'>
        <h2 className='text-xl font-bold text-foreground'>{t('Análisis Detallado')}</h2>
        <p className='text-sm text-default-400'>
          {t('Compara los resultados de ambas temporadas, agrupados por distintos criterios.')}
        </p>
      </CardHeader>
      <CardBody className='p-6 pt-4'>
        <div className='space-y-6'>
          {/* Grouping Tabs */}
          <Tabs
            aria-label='Group by'
            selectedKey={groupBy}
            onSelectionChange={(key) => setGroupBy(key as GroupByKey)}
          >
            {TABS_ORDER.map((key) => (
              <Tab key={key} title={GROUP_BY_OPTIONS[key]} />
            ))}
          </Tabs>

          {/* Charts */}
          <div className='space-y-8'>
            {/* Sales Chart */}
            <div className='space-y-4'>
              <div className='flex w-full justify-between items-start'>
                <div className='text-left'>
                  <div className='flex items-center gap-2'>
                    <h3 className='text-lg font-semibold text-foreground'>
                      {t('Ventas Totales')} {GROUP_BY_OPTIONS[groupBy]}
                    </h3>
                    <ChartDownloadButton
                      chartRef={salesChartRef}
                      title={`${t('Ventas Totales')} ${GROUP_BY_OPTIONS[groupBy]}`}
                      subtitle={`${seasonA} vs ${seasonB}`}
                    />
                  </div>
                  <p className='text-sm text-default-500 mt-1'>
                    {t('Comparación entre')} {seasonA} {t('y')} {seasonB}
                  </p>
                </div>
                <Select
                  label={t('Vista')}
                  value={salesViewMode}
                  defaultSelectedKeys={['absolute']}
                  onChange={(e) =>
                    setSalesViewMode(
                      e.target.value as 'absolute' | 'percentage'
                    )
                  }
                  size='sm'
                  className='w-40'
                >
                  <SelectItem key='absolute' value='absolute'>
                    {t('Valores Absolutos')}
                  </SelectItem>
                  <SelectItem key='percentage' value='percentage'>
                    {t('Participación %')}
                  </SelectItem>
                </Select>
              </div>
              <div ref={salesChartRef}>
                <ComparativeBarChart
                  series={salesChartData.series}
                  categories={salesChartData.categories}
                  title={
                    salesViewMode === 'absolute'
                      ? t('Ventas Totales (USD)')
                      : t('Participación Porcentual (%)')
                  }
                  metric='sales'
                  viewMode={salesViewMode}
                />
              </div>
            </div>

            <Divider />

            {/* Volume Chart */}
            <div className='space-y-4'>
              <div className='flex w-full justify-between items-start'>
                <div className='text-left'>
                  <div className='flex items-center gap-2'>
                    <h3 className='text-lg font-semibold text-foreground'>
                      {t('Volumen Total')} {GROUP_BY_OPTIONS[groupBy]}
                    </h3>
                    <ChartDownloadButton
                      chartRef={volumeChartRef}
                      title={`${t('Volumen Total')} ${GROUP_BY_OPTIONS[groupBy]}`}
                      subtitle={`${seasonA} vs ${seasonB}`}
                    />
                  </div>
                  <p className='text-sm text-default-500 mt-1'>
                    {t('Comparación entre')} {seasonA} {t('y')} {seasonB}
                  </p>
                </div>
                <Select
                  label={t('Vista')}
                  value={volumeViewMode}
                  defaultSelectedKeys={['absolute']}
                  onChange={(e) =>
                    setVolumeViewMode(
                      e.target.value as 'absolute' | 'percentage'
                    )
                  }
                  size='sm'
                  className='w-40'
                >
                  <SelectItem key='absolute' value='absolute'>
                    {t('Valores Absolutos')}
                  </SelectItem>
                  <SelectItem key='percentage' value='percentage'>
                    {t('Participación %')}
                  </SelectItem>
                </Select>
              </div>
              <div ref={volumeChartRef}>
                <ComparativeBarChart
                  series={volumeChartData.series}
                  categories={volumeChartData.categories}
                  title={
                    volumeViewMode === 'absolute'
                      ? t('Volumen Total (kg)')
                      : t('Participación Porcentual (%)')
                  }
                  metric='volume'
                  viewMode={volumeViewMode}
                />
              </div>
            </div>

            <Divider />

            {/* Price Chart */}
            <div className='space-y-4'>
              <div className='text-left'>
                <div className='flex items-center gap-2'>
                  <h3 className='text-lg font-semibold text-foreground'>
                    {t('Precio FOB Promedio')} {GROUP_BY_OPTIONS[groupBy]}
                  </h3>
                  <ChartDownloadButton
                    chartRef={priceChartRef}
                    title={`${t('Precio FOB Promedio')} ${GROUP_BY_OPTIONS[groupBy]}`}
                    subtitle={`${seasonA} vs ${seasonB}`}
                  />
                </div>
                <p className='text-sm text-default-500 mt-1'>
                  {t('Comparación entre')} {seasonA} {t('y')} {seasonB}
                </p>
              </div>
              <div ref={priceChartRef}>
                <ComparativeBarChart
                  series={priceChartData.series}
                  categories={priceChartData.categories}
                  title={`${t('Precio Promedio FOB')} (USD/${priceView === 'kg' ? 'kg' : t('caja eq.')})`}
                  metric='price'
                  viewMode='absolute'
                />
              </div>
            </div>
          </div>
        </div>
      </CardBody>
    </Card>
  );
};

export default ComparativeAnalysisSection;
